// src/components/dashboard/ViewingRequestCard.jsx


import React, { useState } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Box,
  Typography,
  Divider,
  Tooltip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ExpandMore, CheckCircle, Cancel, AccessTime } from "@mui/icons-material";
import ViewingRequestsStyles from "./ViewingRequests.styles";
import { getUserDocsWithLinks } from "../../helpers/getUserDocs"; // <--- Import your helper

const EXCLUDE_FIELDS = ["uid", "submittedAt"]; // never show these

const ViewingRequestCard = ({
  request,
  questionnaire,
  onApprove,
  onDeny,
}) => {
  const [docLoading, setDocLoading] = useState(false);

  const handleViewId = async () => {
    setDocLoading(true);
    try {
      const { idBlobUrl } = await getUserDocsWithLinks(request.uid);
      if (idBlobUrl) {
        window.open(idBlobUrl, "_blank");
      } else {
        alert("No ID document found for this user.");
      }
    } catch (err) {
      alert("Could not load ID document.");
      console.error(err);
    } finally {
      setDocLoading(false);
    }
  };

  const handleViewHeadshot = async () => {
    setDocLoading(true);
    try {
      const { headshotBlobUrl } = await getUserDocsWithLinks(request.uid);
      if (headshotBlobUrl) {
        window.open(headshotBlobUrl, "_blank");
      } else {
        alert("No face document found for this user.");
      }
    } catch (err) {
      alert("Could not load face document.");
      console.error(err);
    } finally {
      setDocLoading(false);
    }
  };

  return (
    <Card sx={ViewingRequestsStyles.viewingCard}>
      <CardMedia
        component="img"
        image={request.image}
        alt="Property Image"
        sx={ViewingRequestsStyles.cardMedia}
      />
      <CardContent sx={ViewingRequestsStyles.cardContent}>
        <Typography sx={ViewingRequestsStyles.dateText}>
          {request.dateDetails?.displayText || "N/A"}
        </Typography>
        <Box sx={ViewingRequestsStyles.statusContainer}>
          {request.status === "approved" ? (
            <CheckCircle
              sx={{
                ...ViewingRequestsStyles.statusIcon,
                color: "#81c241",
              }}
            />
          ) : request.status === "denied" ? (
            <Cancel
              sx={{
                ...ViewingRequestsStyles.statusIcon,
                color: "#333333",
              }}
            />
          ) : (
            <AccessTime
              sx={{
                ...ViewingRequestsStyles.statusIcon,
                color: "#666666",
              }}
            />
          )}
          <Typography
            sx={{
              ...ViewingRequestsStyles.statusText,
              color:
                request.status === "approved"
                  ? "#81c241"
                  : request.status === "denied"
                  ? "#333333"
                  : "#666666",
            }}
          >
            {request.status ? request.status.toUpperCase() : "PENDING"}
          </Typography>
        </Box>
        <Divider sx={ViewingRequestsStyles.divider} />
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
          Property Address
        </Typography>
        <Typography sx={ViewingRequestsStyles.infoText}>
          {request.address}
        </Typography>
        <Typography sx={ViewingRequestsStyles.infoText}>
          {request.city} {request.state}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", mt: 1 }}
        >
          Scheduled By
        </Typography>
        <Typography sx={ViewingRequestsStyles.infoText}>
          {request.name}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", mt: 1 }}
        >
          Contact
        </Typography>
        <Typography sx={ViewingRequestsStyles.infoText}>
          📧 {request.email}
        </Typography>
        <Typography sx={ViewingRequestsStyles.infoText}>
          📞 {request.phone}
        </Typography>

        {/* Questionnaire Section as Accordion */}
        <Divider sx={ViewingRequestsStyles.divider} />
        <Accordion sx={{ boxShadow: "none", bgcolor: "transparent" }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", mb: 0 }}
            >
              Questionnaire
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {questionnaire ? (
              <Box sx={{ pl: 1 }}>
                {Object.entries(questionnaire)
                  .filter(
                    ([key, value]) =>
                      !EXCLUDE_FIELDS.includes(key) &&
                      value !== "" &&
                      value !== undefined
                  )
                  .map(([key, value]) => (
                    <Typography
                      key={key}
                      sx={{
                        ...ViewingRequestsStyles.infoText,
                        fontSize: "0.97rem",
                        mb: 0.3,
                      }}
                    >
                      <b style={{ textTransform: "capitalize" }}>
                        {key.replace(/([A-Z])/g, " $1")}:
                      </b>{" "}
                      {typeof value === "object" && value !== null ? (
                        <pre
                          style={{
                            display: "inline",
                            fontFamily: "inherit",
                            background: "rgba(0,0,0,0.03)",
                            padding: "2px 6px",
                            borderRadius: 4,
                            margin: 0,
                          }}
                        >
                          {JSON.stringify(value, null, 1)}
                        </pre>
                      ) : (
                        String(value)
                      )}
                    </Typography>
                  ))}
              </Box>
            ) : (
              <Typography
                sx={ViewingRequestsStyles.infoText}
                color="text.secondary"
              >
                No questionnaire submitted.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* ---- New View ID/Face Buttons ---- */}
        <Divider sx={ViewingRequestsStyles.divider} />
        <Box sx={{ mt: 1, mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={docLoading}
            onClick={handleViewId}
            sx={{ mr: 1 }}
          >
            {docLoading ? "Loading..." : "View ID"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={docLoading}
            onClick={handleViewHeadshot}
          >
            {docLoading ? "Loading..." : "View Face"}
          </Button>
        </Box>
        {/* --------------------------------- */}

        {/* ----- Zoom Meeting Section (if present) ----- */}
        {request.status === "approved" && request.zoom?.joinUrl && (
          <>
            <Divider sx={ViewingRequestsStyles.divider} />
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Zoom Meeting
            </Typography>
            <Button
              variant="outlined"
              size="small"
              href={request.zoom.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 0.5, textTransform: "none" }}
            >
              Join Meeting
            </Button>
            <Typography sx={{ fontSize: "0.88rem", mt: 0.5, color: "text.secondary" }}>
              {request.zoom.startTime
                ? `Starts: ${new Date(request.zoom.startTime).toLocaleString()}`
                : null}
            </Typography>
            <Typography sx={{ fontSize: "0.88rem", color: "text.secondary" }}>
              Meeting ID: {request.zoom.meetingId}
            </Typography>
          </>
        )}

        {/* Deny Button for Approved Requests */}
        {request.status === "approved" && (
          <>
            <Divider sx={ViewingRequestsStyles.divider} />
            <Box sx={ViewingRequestsStyles.buttonContainer}>
              <Tooltip title="Deny this viewing request">
                <Button
                  variant="contained"
                  sx={ViewingRequestsStyles.denyButton}
                  onClick={() => onDeny && onDeny()}
                >
                  Deny Request
                </Button>
              </Tooltip>
            </Box>
          </>
        )}

        {/* -------------------------------------------- */}

        {/* Pending requests section - commented out (pending tab disabled) */}
        {/* {!request.status && (
          <>
            <Divider sx={ViewingRequestsStyles.divider} />
            <Box sx={ViewingRequestsStyles.buttonContainer}>
              <Tooltip title="Approve and Generate Access Code">
                <Button
                  variant="contained"
                  sx={ViewingRequestsStyles.approveAndGenerateButton}
                  onClick={() => onApprove(request)}
                >
                  Approve & Generate
                </Button>
              </Tooltip>
              <Tooltip title="Deny Viewing Request">
                <Button
                  variant="contained"
                  sx={ViewingRequestsStyles.denyButton}
                  onClick={() => onDeny && onDeny()}
                >
                  Deny
                </Button>
              </Tooltip>
            </Box>
          </>
        )} */}
      </CardContent>
    </Card>
  );
};

export default ViewingRequestCard;
